/**
 * Ganzzahl-Rechnen für Beträge und Dezimalzahlen der Listen (AD-32): Division kaufmännisch gerundet, Abrunden und
 * Preisendungen (,90 · ,99 · ganze). Rein, ohne Gleitkomma; gemeinsam genutzt von `edit.ts` und
 * `purchase-price-rule.ts` (eine Implementierung je Regel, AD-27).
 */

/** Endungen ,90 · ,99 · ganze (Werte wie `listEditRounds`). */
export type EndingRound = 'x90' | 'x99' | 'whole';

/** Ganzzahlige Division, kaufmännisch gerundet (half-up, weg von null). */
export function divHalfUp(n: bigint, d: bigint): bigint {
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (an * 2n + ad) / (ad * 2n);
  return negative ? -q : q;
}

/** Abrunden zur nächstkleineren ganzen Zahl (auch bei negativen Werten). */
export function floorDiv(n: bigint, d: bigint): bigint {
  const q = n / d;
  return (n % d !== 0n && n < 0n !== d < 0n) ? q - 1n : q;
}

/** Richtung von `roundToEnding`: nächstgelegen (Gleichstand aufwärts, Standard) oder aufwärts (kleinster Wert ≥ x). */
export type EndingDirection = 'nearest' | 'up';

/**
 * Wert mit der Endung. `nearest` (Standard): nächstgelegener Wert, Gleichstand aufwärts –
 * floor((x − e + unit/2) / unit) · unit + e. `up` (Story 10.14, Vorschlag für den VK): kleinster Wert mit der Endung, der
 * nicht unter x liegt – ceil((x − e) / unit) · unit + e (x mit passender Endung bleibt).
 * `unit` = Einheiten für „1“ (Cent: 100 = ganzer Euro); Endung ,90/,99 in Hundertsteln davon.
 */
export function roundToEnding(units: bigint, unit: bigint, round: EndingRound, direction: EndingDirection = 'nearest'): bigint {
  const hundredth = unit / 100n;
  const ending = round === 'x90' ? 90n * hundredth : round === 'x99' ? 99n * hundredth : 0n;
  if (direction === 'up') return -floorDiv(-(units - ending), unit) * unit + ending;
  return floorDiv(units - ending + unit / 2n, unit) * unit + ending;
}
